import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, CircleHelp, Folder, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { DRBFMInputData } from '../../../../types';
import SpecificationDocumentPane from './SpecificationDocumentPane';
import SpecificationTextPane from './SpecificationTextPane';
import { InputMode, SpecDocInfo } from './types';

interface SpecificationsCardProps {
  inputMode: InputMode;
  onInputModeChange: (mode: InputMode) => void;
  inputData: DRBFMInputData;
  onFieldChange: (
    field: keyof DRBFMInputData,
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  oldDocInfo: SpecDocInfo | null;
  newDocInfo: SpecDocInfo | null;
  onOldDocSelect: (documentId: string, content: string, fileName: string, title: string) => void;
  onOldDocClear: () => void;
  onNewDocSelect: (documentId: string, content: string, fileName: string, title: string) => void;
  onNewDocClear: () => void;
  validationErrors: Record<string, string>;
  isLoading: boolean;
}

const SpecificationsCard: React.FC<SpecificationsCardProps> = ({
  inputMode,
  onInputModeChange,
  inputData,
  onFieldChange,
  oldDocInfo,
  newDocInfo,
  onOldDocSelect,
  onOldDocClear,
  onNewDocSelect,
  onNewDocClear,
  validationErrors,
  isLoading,
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="p-4 pb-6">
        <div className="mb-2 flex items-center gap-2">
          <ArrowLeftRight className="text-primary" />
          <span className="revy-cjk text-base leading-[1.75]">
            {t('drbfm.input.specifications')}
          </span>
          <TooltipIconButton
            size="icon-sm"
            label={t('drbfm.input.specificationsHelp')}
            icon={<CircleHelp />}
          />
        </div>
        <Separator className="mb-4" />

        {/* Radix activates on focus by default; MUI did not, so arrowing across
            the bar must not swap the pane under the user. */}
        <Tabs
          value={inputMode}
          onValueChange={(value) => onInputModeChange(value as InputMode)}
          activationMode="manual"
        >
          <TabsList className="mb-4 grid w-full grid-cols-2">
            <TabsTrigger value="document" className="revy-cjk gap-2">
              <Folder className="size-4" />
              {t('drbfm.input.documentMode', 'ドキュメント選択')}
            </TabsTrigger>
            <TabsTrigger value="text" className="revy-cjk gap-2">
              <Pencil className="size-4" />
              {t('drbfm.input.textMode', 'テキスト入力')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="document" className="mt-0">
            <div className="grid grid-cols-1 gap-4 min-[900px]:grid-cols-2">
              <SpecificationDocumentPane
                heading={t('drbfm.input.oldSpecification')}
                label={t('drbfm.input.selectOldSpec', '旧仕様書を選択')}
                selectedDocumentId={inputData.specOldDocumentId}
                docInfo={oldDocInfo}
                onSelect={onOldDocSelect}
                onClear={onOldDocClear}
                error={validationErrors.oldSpecification}
                disabled={isLoading}
              />
              <SpecificationDocumentPane
                heading={t('drbfm.input.newSpecification')}
                label={t('drbfm.input.selectNewSpec', '新仕様書を選択')}
                selectedDocumentId={inputData.specNewDocumentId}
                docInfo={newDocInfo}
                onSelect={onNewDocSelect}
                onClear={onNewDocClear}
                error={validationErrors.newSpecification}
                disabled={isLoading}
              />
            </div>
          </TabsContent>

          <TabsContent value="text" className="mt-0">
            <div className="grid grid-cols-1 gap-4 min-[900px]:grid-cols-2">
              <SpecificationTextPane
                heading={t('drbfm.input.oldSpecification')}
                value={inputData.oldSpecification}
                onChange={onFieldChange('oldSpecification')}
                placeholder={t('drbfm.input.oldSpecPlaceholder')}
                error={validationErrors.oldSpecification}
              />
              <SpecificationTextPane
                heading={t('drbfm.input.newSpecification')}
                value={inputData.newSpecification}
                onChange={onFieldChange('newSpecification')}
                placeholder={t('drbfm.input.newSpecPlaceholder')}
                error={validationErrors.newSpecification}
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SpecificationsCard;
